import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpSnackbarAlerts from '../../../shared/components/display/ExpSnackbarAlerts';
import useStagingReview from '../hooks/useStagingReview';
import StagingStagedDetails from './components/StagingStagedDetails';
import StagingCandidatesList from './components/StagingCandidatesList';
import StagingDiffViewer from './components/StagingDiffViewer';

const StagingReviewModal = ({ open, onClose, stagedVoyage, onResolved }) => {
  const {
    candidates,
    loadingCandidates,
    selectedCandidate,
    diffData,
    loadingDiff,
    searchQuery,
    setSearchQuery,
    loadingSearch,
    actionLoading,
    snackbar,
    closeSnackbar,
    view,
    mergeMode,
    fieldSelections,
    shipmentSelections,
    shipmentFieldSelections,
    hasAnySelection,
    selectionSummary,
    handleViewDiff,
    handleSearch,
    handleConfirmMatch,
    handleCreateAsNew,
    handleReject,
    handleFieldSelectionChange,
    handleShipmentSelectionChange,
    handleShipmentFieldSelectionChange,
    setMergeModeValue,
    resetSelections,
    goBackToCandidates,
  } = useStagingReview({ open, stagedVoyage, onResolved, onClose });

  if (!stagedVoyage) return null;

  return (
    <>
      <ExpSnackbarAlerts
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={closeSnackbar}
      />
      <Dialog open={open} onClose={onClose} maxWidth='xl' fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='h6'>
            Review Staged Voyage —{' '}
            {stagedVoyage.voyageNumber || stagedVoyage.id}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 3, minHeight: 500 }}>
            {/* Left Panel */}
            <StagingStagedDetails stagedVoyage={stagedVoyage} />

            {/* Right Panel */}
            <Box sx={{ width: '60%' }}>
              {view === 'candidates' && (
                <StagingCandidatesList
                  stagedVoyage={stagedVoyage}
                  candidates={candidates}
                  loadingCandidates={loadingCandidates}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  loadingSearch={loadingSearch}
                  loadingDiff={loadingDiff}
                  actionLoading={actionLoading}
                  onSearch={handleSearch}
                  onViewDiff={handleViewDiff}
                  onCreateAsNew={handleCreateAsNew}
                />
              )}

              {view === 'diff' && (
                <StagingDiffViewer
                  selectedCandidate={selectedCandidate}
                  diffData={diffData}
                  mergeMode={mergeMode}
                  fieldSelections={fieldSelections}
                  shipmentSelections={shipmentSelections}
                  shipmentFieldSelections={shipmentFieldSelections}
                  hasAnySelection={hasAnySelection}
                  selectionSummary={selectionSummary}
                  onFieldSelectionChange={handleFieldSelectionChange}
                  onShipmentSelectionChange={handleShipmentSelectionChange}
                  onShipmentFieldSelectionChange={
                    handleShipmentFieldSelectionChange
                  }
                  onSetMergeMode={setMergeModeValue}
                  onResetSelections={resetSelections}
                  onGoBack={goBackToCandidates}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Button
            variant='outlined'
            color='error'
            onClick={handleReject}
            disabled={actionLoading}
          >
            Reject
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {view === 'diff' && selectedCandidate && (
              <Button
                variant='contained'
                color='primary'
                onClick={handleConfirmMatch}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <CircularProgress size={20} />
                ) : mergeMode === 'selective' ? (
                  'Confirm Selective Merge'
                ) : (
                  'Confirm Match (Take All)'
                )}
              </Button>
            )}
            <Button onClick={onClose}>Close</Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StagingReviewModal;
